import { createSelector, createSlice } from '@reduxjs/toolkit';
import { Driver } from '../models/driver.model';
import { DriverLocation } from '../models/driver-location.model';
import { driverAdapter } from '../entities/driver.entity';
import driverService from '../../services/driverService';
import type { AppDispatch, RootState } from '../store';

type DriverEntityState = {
  // Drivers that have been fetched in full by ID. Drivers can also land in the
  // adapter as partial data embedded in other payloads (e.g. lean websocket
  // events), so presence in the adapter alone doesn't mean a fetch is
  // unnecessary.
  loadedDriverIDs: Record<number, boolean>;
  loadingDriverIDs: Record<number, boolean>;
  // Driver IDs per provider, in the order the API returned them. Kept as an ID
  // list rather than filtering the adapter by provider, since the adapter also
  // holds drivers from other sources (trip legs, reservations) that the
  // provider list may intentionally exclude.
  driverIDsByProvider: Record<number, number[]>;
  loadingDriversByProvider: boolean;
  loadedDriversByProvider: boolean;
  driverLocation: DriverLocation | null;
  driverLocationsById: Record<number, DriverLocation>;
  driverLocationsByTripLegId: Record<number, DriverLocation>;
  loadingDriverLocation: boolean;
  loadedDriverLocation: boolean;
};

// Driver data arrives embedded in many payloads (trip legs, reservations,
// websocket events) where it's frequently null, so drop anything that can't be
// keyed instead of making every caller filter first.
const isDriver = (driver: Driver | null | undefined): driver is Driver =>
  driver?.driverID != null;

const driverSlice = createSlice({
  name: 'driver',
  initialState: driverAdapter.getInitialState<DriverEntityState>({
    loadedDriverIDs: {},
    loadingDriverIDs: {},
    driverIDsByProvider: {},
    loadingDriversByProvider: false,
    loadedDriversByProvider: false,
    driverLocation: null,
    driverLocationsById: {},
    driverLocationsByTripLegId: {},
    loadingDriverLocation: false,
    loadedDriverLocation: false,
  }),
  reducers: {
    // Driver entity related actions
    upsertDrivers: (
      state,
      action: { payload: (Driver | null | undefined)[] },
    ) => {
      driverAdapter.upsertMany(state, action.payload.filter(isDriver));
    },
    upsertDriver: (state, action: { payload: Driver | null | undefined }) => {
      if (isDriver(action.payload)) {
        driverAdapter.upsertOne(state, action.payload);
      }
    },
    driverLoaded: (state, action: { payload: Driver }) => {
      driverAdapter.upsertOne(state, action.payload);
      state.loadedDriverIDs[action.payload.driverID] = true;
    },
    setLoadingDriverById: (
      state,
      action: { payload: { driverID: number; loading: boolean } },
    ) => {
      const { driverID, loading } = action.payload;
      if (loading) {
        state.loadingDriverIDs[driverID] = true;
      } else {
        delete state.loadingDriverIDs[driverID];
      }
    },

    // Drivers by provider related actions
    setDriversByProvider: (
      state,
      action: { payload: { providerId: number; drivers: Driver[] } },
    ) => {
      const { providerId, drivers } = action.payload;
      driverAdapter.upsertMany(state, drivers);
      state.driverIDsByProvider[providerId] = drivers.map(
        (driver) => driver.driverID,
      );
    },
    setLoadingDriversByProvider: (state, action: { payload: boolean }) => {
      state.loadingDriversByProvider = action.payload;
    },
    setLoadedDriversByProvider: (state, action: { payload: boolean }) => {
      state.loadedDriversByProvider = action.payload;
    },

    // Driver location related actions
    setDriverLocation: (state, action: { payload: DriverLocation }) => {
      const location = action.payload;
      state.driverLocation = location;
      if (location?.driverID) {
        state.driverLocationsById[location.driverID] = location;
      }
      if (location?.tripLegID) {
        state.driverLocationsByTripLegId[location.tripLegID] = location;
      }
    },
    setLoadingDriverLocation: (state, action: { payload: boolean }) => {
      state.loadingDriverLocation = action.payload;
    },
    setLoadedDriverLocation: (state, action: { payload: boolean }) => {
      state.loadedDriverLocation = action.payload;
    },
    clearDriverLocation: (state) => {
      state.driverLocation = null;
    },
  },
});

export const {
  upsertDrivers,
  upsertDriver,
  driverLoaded,
  setLoadingDriverById,
  setDriversByProvider,
  setLoadingDriversByProvider,
  setLoadedDriversByProvider,
  setDriverLocation,
  setLoadingDriverLocation,
  setLoadedDriverLocation,
  clearDriverLocation,
} = driverSlice.actions;

/*
 * Fetch functions for use in components to fetch drivers from the API
 */

const fetchDriverByIdAsync =
  (driverID: number) => (dispatch: AppDispatch, getState: () => RootState) => {
    const { loadedDriverIDs, loadingDriverIDs } = getState().driver;
    if (loadedDriverIDs[driverID] || loadingDriverIDs[driverID]) {
      return;
    }
    dispatch(setLoadingDriverById({ driverID, loading: true }));
    driverService.getDriverByID(driverID).subscribe({
      next: (driver) => {
        dispatch(driverLoaded(driver));
        dispatch(setLoadingDriverById({ driverID, loading: false }));
      },
      error: () => {
        dispatch(setLoadingDriverById({ driverID, loading: false }));
      },
    });
  };

const fetchDriversByProviderAsync =
  (providerId: number) => (dispatch: AppDispatch) => {
    dispatch(setLoadingDriversByProvider(true));
    dispatch(setLoadedDriversByProvider(false));
    driverService
      .getDriversByProvider({
        providerId,
        pageSize: 1000,
        sortModel: {
          column: 'driverFirstName,driverLastName',
          direction: 'asc,asc',
        },
      })
      .subscribe({
        next: (pagedList) => {
          dispatch(
            setDriversByProvider({ providerId, drivers: pagedList.items }),
          );
          dispatch(setLoadingDriversByProvider(false));
          dispatch(setLoadedDriversByProvider(true));
        },
        error: () => {
          dispatch(setLoadingDriversByProvider(false));
        },
      });
  };

const fetchDriverLocationAsync =
  (driverID: number) => (dispatch: AppDispatch) => {
    dispatch(setLoadingDriverLocation(true));
    dispatch(setLoadedDriverLocation(false));
    driverService.getDriverLocation(driverID).subscribe({
      next: (location) => {
        dispatch(setDriverLocation(location));
        dispatch(setLoadingDriverLocation(false));
        dispatch(setLoadedDriverLocation(true));
      },
      error: () => {
        dispatch(setLoadingDriverLocation(false));
      },
    });
  };

/*
 * Selectors
 * This slice is the source of truth for driver data. Other entity slices that
 * embed a driver (e.g. trip legs) should merge from here rather than reading
 * their own copy, so realtime driver updates are reflected everywhere.
 */

const driverSelectors = driverAdapter.getSelectors<RootState>(
  (state) => state.driver,
);

const selectDriverEntities = (state: RootState) =>
  driverSelectors.selectEntities(state);

const selectAllDrivers = (state: RootState) => driverSelectors.selectAll(state);

const selectDriverById = (
  state: RootState,
  driverID: number | null | undefined,
) =>
  driverID == null ? undefined : driverSelectors.selectById(state, driverID);

const EMPTY_DRIVERS: Driver[] = [];

const selectDriversByProvider = createSelector(
  [
    selectDriverEntities,
    (state: RootState, providerId: number) =>
      state.driver.driverIDsByProvider[providerId],
  ],
  (drivers, driverIDs) =>
    driverIDs
      ? driverIDs
          .map((driverID) => drivers[driverID])
          .filter((driver): driver is Driver => !!driver)
      : EMPTY_DRIVERS,
);

const selectLoadingDriversByProvider = (state: RootState) =>
  state.driver.loadingDriversByProvider;
const selectLoadedDriversByProvider = (state: RootState) =>
  state.driver.loadedDriversByProvider;

const selectDriverLocation = (state: RootState) => state.driver.driverLocation;
const selectDriverLocationsById = (state: RootState) =>
  state.driver.driverLocationsById;
const selectDriverLocationsByTripLegId = (state: RootState) =>
  state.driver.driverLocationsByTripLegId;
const selectLoadingDriverLocation = (state: RootState) =>
  state.driver.loadingDriverLocation;
const selectLoadedDriverLocation = (state: RootState) =>
  state.driver.loadedDriverLocation;

export {
  driverSlice,
  driverSelectors,
  fetchDriverByIdAsync,
  fetchDriversByProviderAsync,
  fetchDriverLocationAsync,
  selectDriverEntities,
  selectAllDrivers,
  selectDriverById,
  selectDriversByProvider,
  selectLoadingDriversByProvider,
  selectLoadedDriversByProvider,
  selectDriverLocation,
  selectDriverLocationsById,
  selectDriverLocationsByTripLegId,
  selectLoadingDriverLocation,
  selectLoadedDriverLocation,
};
